import React, { useState } from "react";
import emailjs from "@emailjs/browser";

const Contact = () => {
  const [loading, setLoading] = useState(false);

  const [status, setStatus] = useState({
    type: "",
    message: "",
  });

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
  });

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));

    if (status.message) {
      setStatus({
        type: "",
        message: "",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setStatus({
        type: "error",
        message: "Please enter a valid email address.",
      });

      return;
    }

    setLoading(true);

    setStatus({
      type: "",
      message: "",
    });

    const serviceID = "service_vgs294g";
    const templateID = "template_2tn2dde";
    const userID = "rpmyDEHEMrEZ5nS1z";

    try {
      await emailjs.send(serviceID, templateID, formData, userID);

      const acknowledgmentTemplateID = "template_qcwhpg8";

      await emailjs.send(
        serviceID,
        acknowledgmentTemplateID,
        {
          email: formData.email,
          name: formData.firstName,
        },
        userID,
      );

      setStatus({
        type: "success",
        message:
          "Message transmitted. An acknowledgement has been sent to your inbox.",
      });

      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        message: "",
      });
    } catch (error) {
      setStatus({
        type: "error",
        message: "Transmission failed. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#080B10] text-[#EDEFF2]">
      {/* =====================================================
          BACKGROUND
      ===================================================== */}

      <div className="pointer-events-none fixed inset-0">
        {/* Grid */}
        <div
          className="
            absolute inset-0
            opacity-[0.08]
            [background-image:linear-gradient(rgba(255,255,255,.12)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.12)_1px,transparent_1px)]
            [background-size:48px_48px]
          "
        />

        {/* Teal glow */}
        <div className="absolute right-[5%] top-[10%] h-96 w-96 rounded-full bg-[#4CD8B8]/5 blur-[140px]" />

        {/* Gold glow */}
        <div className="absolute bottom-[5%] left-[5%] h-80 w-80 rounded-full bg-[#E3A857]/5 blur-[130px]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:px-10">
        {/* =====================================================
            HEADER
        ===================================================== */}

        <section className="mb-12 max-w-3xl">
          <div className="mb-6 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#4CD8B8]">
            <span className="h-px w-8 bg-[#4CD8B8]" />
            communication / open
          </div>

          <h1 className="text-5xl font-semibold leading-[0.95] tracking-[-0.045em] text-white sm:text-6xl">
            Start a
            <br />
            <span className="text-white/25">conversation.</span>
          </h1>

          <p className="mt-7 max-w-2xl text-base leading-8 text-white/40 sm:text-lg">
            Have an interesting idea, technical question, collaboration or
            project worth discussing? Send me a message.
          </p>
        </section>

        {/* =====================================================
            MAIN GRID
        ===================================================== */}

        <div className="grid gap-5 lg:grid-cols-[0.72fr_1.28fr]">
          {/* =================================================
              LEFT INFORMATION PANEL
          ================================================= */}

          <aside className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#0D1117] p-7 sm:p-8">
            {/* Glow */}
            <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-[#4CD8B8]/5 blur-[80px]" />

            <div className="relative">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/25">
                  contact_node
                </span>

                <span className="flex items-center gap-2 font-mono text-[8px] uppercase tracking-[0.15em] text-[#4CD8B8]/60">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#4CD8B8]" />
                  online
                </span>
              </div>

              {/* Email */}
              <div className="mt-12 border-b border-white/10 pb-7">
                <div className="mb-3 font-mono text-[9px] uppercase tracking-[0.2em] text-[#4CD8B8]">
                  email
                </div>

                <a
                  href="mailto:stephen@stephenadegbile.uk"
                  className="group flex items-center gap-3 text-sm text-white/65 transition-colors hover:text-[#4CD8B8]"
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03]">
                    <svg
                      className="h-4 w-4 text-[#4CD8B8]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                      />
                    </svg>
                  </div>

                  <span className="break-all">stephen@stephenadegbile.uk</span>

                  <span className="opacity-0 transition-opacity group-hover:opacity-100">
                    ↗
                  </span>
                </a>
              </div>

              {/* Response */}
              <div className="border-b border-white/10 py-7">
                <div className="mb-3 font-mono text-[9px] uppercase tracking-[0.2em] text-[#4CD8B8]">
                  response
                </div>

                <div className="flex items-center gap-3">
                  <div className="font-mono text-2xl text-white">24–48</div>

                  <div className="text-xs text-white/30">hours</div>
                </div>
              </div>

              {/* Focus */}
              <div className="py-7">
                <div className="mb-4 font-mono text-[9px] uppercase tracking-[0.2em] text-[#4CD8B8]">
                  conversation areas
                </div>

                <div className="flex flex-wrap gap-2">
                  {[
                    "Data Engineering",
                    "Cloud",
                    "AI Systems",
                    "Automation",
                    "Software",
                  ].map((item) => (
                    <span
                      key={item}
                      className="
                        rounded-full
                        border border-white/10
                        bg-white/[0.025]
                        px-3 py-1.5
                        font-mono
                        text-[8px]
                        uppercase
                        tracking-[0.12em]
                        text-white/30
                      "
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              {/* System footer */}
              <div className="mt-8 rounded-2xl border border-white/5 bg-black/20 p-4">
                <div className="flex items-center justify-between font-mono text-[8px] uppercase tracking-[0.15em]">
                  <span className="text-white/20">channel</span>

                  <span className="text-[#4CD8B8]/60">encrypted / active</span>
                </div>
              </div>
            </div>
          </aside>

          {/* =================================================
              FORM PANEL
          ================================================= */}

          <section className="rounded-3xl border border-white/10 bg-[#0D1117] p-7 sm:p-8 lg:p-10">
            <div className="mb-8 flex items-start justify-between gap-5">
              <div>
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/25">
                  message_interface
                </div>

                <h2 className="mt-3 text-2xl font-semibold text-white">
                  Send a message
                </h2>

                <p className="mt-2 text-sm leading-6 text-white/35">
                  Tell me what you're working on.
                </p>
              </div>

              <div className="hidden font-mono text-[9px] text-white/15 sm:block">
                FORM_001
              </div>
            </div>

            {/* Status */}
            {status.message && (
              <div
                className={`
                  mb-6 flex items-center gap-3
                  rounded-2xl
                  border
                  px-4 py-4
                  font-mono
                  text-xs
                  ${
                    status.type === "success"
                      ? "border-[#4CD8B8]/25 bg-[#4CD8B8]/5 text-[#4CD8B8]"
                      : "border-red-500/25 bg-red-500/5 text-red-300"
                  }
                `}
              >
                <span
                  className={`
                    flex h-7 w-7 items-center justify-center
                    rounded-full
                    ${
                      status.type === "success"
                        ? "bg-[#4CD8B8]/10"
                        : "bg-red-500/10"
                    }
                  `}
                >
                  {status.type === "success" ? "✓" : "!"}
                </span>

                {status.message}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Names */}
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block font-mono text-[9px] uppercase tracking-[0.15em] text-white/30">
                    First name
                  </label>

                  <input
                    type="text"
                    name="firstName"
                    placeholder="Stephen"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="
                      w-full rounded-2xl
                      border border-white/10
                      bg-[#080B10]
                      px-4 py-4
                      text-sm text-white
                      outline-none
                      placeholder:text-white/15
                      transition-all duration-300
                      focus:border-[#4CD8B8]/50
                      focus:bg-[#0A0E13]
                      focus:ring-1
                      focus:ring-[#4CD8B8]/10
                    "
                  />
                </div>

                <div>
                  <label className="mb-2 block font-mono text-[9px] uppercase tracking-[0.15em] text-white/30">
                    Last name
                  </label>

                  <input
                    type="text"
                    name="lastName"
                    placeholder="Adegbile"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="
                      w-full rounded-2xl
                      border border-white/10
                      bg-[#080B10]
                      px-4 py-4
                      text-sm text-white
                      outline-none
                      placeholder:text-white/15
                      transition-all duration-300
                      focus:border-[#4CD8B8]/50
                      focus:bg-[#0A0E13]
                      focus:ring-1
                      focus:ring-[#4CD8B8]/10
                    "
                  />
                </div>
              </div>

              {/* Email / phone */}
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block font-mono text-[9px] uppercase tracking-[0.15em] text-white/30">
                    Email address
                  </label>

                  <input
                    type="email"
                    name="email"
                    placeholder="name@company.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="
                      w-full rounded-2xl
                      border border-white/10
                      bg-[#080B10]
                      px-4 py-4
                      text-sm text-white
                      outline-none
                      placeholder:text-white/15
                      transition-all duration-300
                      focus:border-[#4CD8B8]/50
                      focus:bg-[#0A0E13]
                      focus:ring-1
                      focus:ring-[#4CD8B8]/10
                    "
                  />
                </div>

                <div>
                  <label className="mb-2 block font-mono text-[9px] uppercase tracking-[0.15em] text-white/30">
                    Phone
                    <span className="ml-2 text-white/15">optional</span>
                  </label>

                  <input
                    type="tel"
                    name="phone"
                    placeholder="+44..."
                    value={formData.phone}
                    onChange={handleChange}
                    className="
                      w-full rounded-2xl
                      border border-white/10
                      bg-[#080B10]
                      px-4 py-4
                      text-sm text-white
                      outline-none
                      placeholder:text-white/15
                      transition-all duration-300
                      focus:border-[#4CD8B8]/50
                      focus:bg-[#0A0E13]
                      focus:ring-1
                      focus:ring-[#4CD8B8]/10
                    "
                  />
                </div>
              </div>

              {/* Message */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="font-mono text-[9px] uppercase tracking-[0.15em] text-white/30">
                    Message
                  </label>

                  <span className="font-mono text-[8px] text-white/15">
                    required
                  </span>
                </div>

                <textarea
                  name="message"
                  rows="7"
                  placeholder="Tell me about the idea, project, technical problem or opportunity..."
                  value={formData.message}
                  onChange={handleChange}
                  required
                  className="
                    w-full resize-none
                    rounded-2xl
                    border border-white/10
                    bg-[#080B10]
                    px-4 py-4
                    text-sm leading-7 text-white
                    outline-none
                    placeholder:text-white/15
                    transition-all duration-300
                    focus:border-[#4CD8B8]/50
                    focus:bg-[#0A0E13]
                    focus:ring-1
                    focus:ring-[#4CD8B8]/10
                  "
                />
              </div>

              {/* Submit */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="
                    group
                    relative
                    flex w-full
                    items-center
                    justify-center
                    gap-3
                    overflow-hidden
                    rounded-2xl
                    border border-[#4CD8B8]/40
                    bg-[#4CD8B8]
                    px-6 py-4
                    font-mono
                    text-xs
                    font-semibold
                    uppercase
                    tracking-[0.15em]
                    text-[#080B10]
                    transition-all duration-300
                    hover:brightness-110
                    hover:shadow-[0_0_35px_rgba(76,216,184,0.15)]
                    disabled:cursor-not-allowed
                    disabled:opacity-50
                  "
                >
                  <span className="relative z-10">
                    {loading ? "Transmitting..." : "Transmit message"}
                  </span>

                  {!loading && (
                    <span className="relative z-10 text-base transition-transform duration-300 group-hover:translate-x-1">
                      →
                    </span>
                  )}

                  {/* Animated signal */}
                  <span
                    className="
                      absolute left-0 top-0
                      h-full w-20
                      -translate-x-full
                      bg-white/20
                      blur-xl
                      transition-transform duration-700
                      group-hover:translate-x-[600%]
                    "
                  />
                </button>
              </div>
            </form>

            {/* Bottom metadata */}
            <div className="mt-6 flex flex-col justify-between gap-2 border-t border-white/5 pt-5 font-mono text-[8px] uppercase tracking-[0.15em] text-white/15 sm:flex-row">
              <span>endpoint / stephenadegbile.uk</span>

              <span>response / 24–48h</span>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-16 flex items-center justify-between border-t border-white/10 pt-5 font-mono text-[9px] uppercase tracking-[0.2em] text-white/20">
          <span>contact / communication channel</span>

          <span className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#4CD8B8]" />
            channel open
          </span>
        </div>
      </div>
    </main>
  );
};

export default Contact;
